import { useState, useRef } from "react";
import { BRAND, Section } from "../brand";

const CATEGORIES = [
  {
    id: "scenario",
    title: "By Call Type",
    icon: "📞",
    demos: [
      {
        name: "Reservation",
        description: "Booking a table, party size, special requests",
        color: "#F97316",
        avatar: "📅",
        audioSrc: "/audio/demos/by-type/reservation.mp3",
      },
      {
        name: "Delivery & Takeout",
        description: "Placing orders, sending links via SMS",
        color: "#EC4899",
        avatar: "🥡",
        audioSrc: "/audio/demos/by-type/takeout.mp3",
      },
      {
        name: "General FAQ",
        description: "Hours, location, parking, menu",
        color: "#8B5CF6",
        avatar: "💬",
        audioSrc: "/audio/demos/by-type/general.mp3",
      },
      {
        name: "Catering & Events",
        description: "Large parties, private dining",
        color: "#D946EF",
        avatar: "🎉",
        audioSrc: "/audio/demos/by-type/catering.mp3",
      },
      {
        name: "Events & Tickets",
        description: "Event info + ticket purchase link via SMS",
        color: "#F59E0B",
        avatar: "🎟️",
        audioSrc: "/audio/demos/by-type/events.mp3",
      },
    ],
  },
  {
    id: "type",
    title: "By Restaurant",
    icon: "🍽️",
    demos: [
      {
        name: "Nacho",
        description: "Rreal Tacos · Mexican",
        color: "#F97316",
        avatar: "🌮",
        audioSrc: "/audio/demos/by-restaurant/rreal-tacos.mp3",
      },
      {
        name: "Lucy",
        description: "Baires Grill · Argentine Steakhouse",
        color: "#8B5CF6",
        avatar: "🥩",
        audioSrc: "/audio/demos/by-restaurant/baires-grill.mp3",
      },
      {
        name: "Sara",
        description: "La Cañita Kendall · Cuban Cuisine",
        color: "#10B981",
        avatar: "💼",
        audioSrc: "/audio/demos/by-restaurant/jobs.mp3",
      },
      {
        name: "Nicole",
        description: "Tao Group · Hospitality",
        color: "#F59E0B",
        avatar: "🍸",
        audioSrc: "/audio/demos/by-restaurant/tao-group.mp3",
      },
      {
        name: "Hope",
        description: "Pura Vida Miami · Healthy Fast-Casual",
        color: "#22C55E",
        avatar: "🥗",
        audioSrc: "/audio/demos/by-restaurant/pura-vida.mp3",
      },
    ],
  },
  {
    id: "language",
    title: "By Language",
    icon: "🌍",
    demos: [
      {
        name: "English",
        description: "Native English interaction",
        color: "#F97316",
        avatar: "🇺🇸",
        audioSrc: "/audio/demos/by-language/english.mp3",
      },
      {
        name: "Español",
        description: "Conversación completa en español",
        color: "#EC4899",
        avatar: "🇪🇸",
        audioSrc: "/audio/demos/by-language/spanish.mp3",
      },
    ],
  },
];

export default function DemoSlide({ isMobile, isDemo }) {
  const [activeCategory, setActiveCategory] = useState("scenario");
  const [playingDemo, setPlayingDemo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioError, setAudioError] = useState(null);
  const audioRef = useRef(null);

  const category = CATEGORIES.find((c) => c.id === activeCategory);

  const handlePlay = (demo) => {
    const audio = audioRef.current;

    if (playingDemo === demo.name && isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    if (playingDemo === demo.name && !isPlaying) {
      audio.play().catch(() => setAudioError(demo.name));
      setIsPlaying(true);
      return;
    }

    // New demo
    setAudioError(null);
    setProgress(0);
    setDuration(0);
    audio.src = demo.audioSrc;
    audio.load();
    setPlayingDemo(demo.name);
    audio.play().catch(() => {
      setAudioError(demo.name);
      setIsPlaying(false);
    });
    setIsPlaying(true);
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio.duration) {
      setProgress((audio.currentTime / audio.duration) * 100);
      setDuration(audio.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const formatTime = (s) => {
    if (!s) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <Section style={{ background: BRAND.dark }}>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={() => setAudioError(playingDemo)}
        preload="none"
      />
      <div style={{ maxWidth: 880, margin: "0 auto", width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 24 : 32 }}>
          <div
            style={{
              fontSize: 13,
              letterSpacing: 3,
              color: BRAND.orange,
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            {isDemo ? "Hear the AI in action" : "What you heard in our meeting"}
          </div>
          <h2
            style={{
              fontSize: isMobile ? 28 : 44,
              fontWeight: 800,
              color: BRAND.white,
              margin: 0,
              fontFamily: "'SF Pro Rounded', 'Manrope', sans-serif",
            }}
          >
            Your AI Host in action
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: isMobile ? 15 : 17,
              marginTop: 10,
            }}
          >
            {isDemo ? "Tap any demo to replay the call" : "Tap to replay any demo from our conversation"}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            marginBottom: isMobile ? 20 : 28,
            flexWrap: "wrap",
          }}
        >
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(cat.id);
                if (audioRef.current) {
                  audioRef.current.pause();
                  audioRef.current.src = "";
                }
                setPlayingDemo(null);
                setIsPlaying(false);
                setProgress(0);
                setAudioError(null);
              }}
              style={{
                padding: isMobile ? "9px 16px" : "11px 22px",
                borderRadius: 100,
                border: "none",
                fontSize: isMobile ? 13 : 14.5,
                fontWeight: 600,
                cursor: "pointer",
                background:
                  activeCategory === cat.id
                    ? BRAND.gradient
                    : "rgba(255,255,255,0.06)",
                color: BRAND.white,
              }}
            >
              {cat.icon} {cat.title}
            </button>
          ))}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : activeCategory === "language"
                ? "repeat(2, 1fr)"
                : "repeat(2, 1fr)",
            gap: 14,
            maxWidth:
              !isMobile && activeCategory === "language" ? 600 : "100%",
            margin: "0 auto",
          }}
        >
          {category.demos.map((demo, i) => {
            const isA = playingDemo === demo.name && isPlaying;
            const hasError = audioError === demo.name;

            return (
              <button
                key={`${activeCategory}-${i}`}
                onClick={() => handlePlay(demo)}
                style={{
                  background: isA
                    ? `linear-gradient(135deg, ${demo.color}18, ${demo.color}08)`
                    : "rgba(255,255,255,0.03)",
                  border: isA
                    ? `2px solid ${demo.color}60`
                    : "2px solid rgba(255,255,255,0.06)",
                  borderRadius: 20,
                  padding: isMobile ? "20px 18px" : "26px 22px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  textAlign: "left",
                  transform: isA ? "scale(1.015)" : "scale(1)",
                  transition: "all 0.35s ease",
                  width: "100%",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Progress bar at bottom */}
                {isA && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      height: 3,
                      background: demo.color,
                      width: `${progress}%`,
                      transition: "width 0.1s linear",
                      borderRadius: "0 2px 0 0",
                    }}
                  />
                )}
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 16,
                    flexShrink: 0,
                    background: isA
                      ? `linear-gradient(135deg, ${demo.color}, ${demo.color}BB)`
                      : "rgba(255,255,255,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 26,
                  }}
                >
                  {isA ? (
                    <div
                      style={{
                        display: "flex",
                        gap: 3,
                        alignItems: "flex-end",
                        height: 22,
                      }}
                    >
                      {[10, 18, 14, 20, 12].map((h, j) => (
                        <div
                          key={j}
                          style={{
                            width: 3,
                            borderRadius: 2,
                            background: "#fff",
                            height: h,
                            animation: `audioBar 0.7s ease-in-out ${j * 0.08}s infinite alternate`,
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    demo.avatar
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: isMobile ? 15 : 17,
                      fontWeight: 700,
                      color: isA ? "#fff" : "rgba(255,255,255,0.85)",
                      marginBottom: 3,
                    }}
                  >
                    {demo.name}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? 12 : 13.5,
                      color: isA
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(255,255,255,0.35)",
                    }}
                  >
                    {hasError ? (
                      <span style={{ color: `${demo.color}80` }}>
                        Demo coming soon
                      </span>
                    ) : isA && duration ? (
                      `${formatTime(audioRef.current?.currentTime)} / ${formatTime(duration)}`
                    ) : (
                      demo.description
                    )}
                  </div>
                </div>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: isA
                      ? "rgba(255,255,255,0.15)"
                      : `${demo.color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      color: isA ? "#fff" : demo.color,
                      fontSize: 16,
                    }}
                  >
                    {hasError ? "—" : isA ? "⏸" : "▶"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
